import type { ReactElement } from "react";

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import {
  Coins,
  Crown,
  Check,
  Loader2,
  Sparkles,
  X,
  ArrowUp,
  ArrowDown,
  Volume2,
  Palette,
  ImageIcon,
  Zap,
  RotateCcw,
  AlertTriangle,
  Library,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * User data from the auth endpoint
 */
interface User {
  credits: number;
  is_premium: boolean;
  subscription_plan?: string;
}

/**
 * Credit package from the payments API
 */
interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  price_formatted: string;
}

/**
 * Subscription plan from the payments API
 */
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  credits_per_month: number;
}

/**
 * Current subscription status
 */
interface Subscription {
  has_subscription: boolean;
  plan?: string;
  plan_name?: string;
  cancel_at_period_end?: boolean;
  current_period_end?: string;
}

/**
 * Switch modal state
 */
interface SwitchModalState {
  show: boolean;
  plan: SubscriptionPlan | null;
  isUpgrade: boolean;
}

/**
 * Premium feature item
 */
interface PremiumFeature {
  icon: LucideIcon;
  label: string;
  desc: string;
  isSeries?: boolean;
}

/**
 * FAQ item
 */
interface FAQItem {
  q: string;
  a: string;
}

type TabType = "credits" | "subscription";

/**
 * PricingPage Component
 *
 * Displays credit packs and subscription plans for purchase.
 */
const PricingPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [switchingPlan, setSwitchingPlan] = useState<string | null>(null);
  const [cancelingSubscription, setCancelingSubscription] =
    useState<boolean>(false);
  const [reactivatingSubscription, setReactivatingSubscription] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabType>(
    (searchParams.get("tab") as TabType) || "credits"
  );
  const [switchModal, setSwitchModal] = useState<SwitchModalState>({
    show: false,
    plan: null,
    isUpgrade: false,
  });
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      const userRes = await axios.get<User>(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(userRes.data);

      const [packagesRes, plansRes] = await Promise.all([
        axios.get<{ packages: CreditPackage[] }>(`${API}/v1/payments/packages`, {
          withCredentials: true,
        }),
        axios.get<{ plans: SubscriptionPlan[] }>(`${API}/v1/payments/plans`, {
          withCredentials: true,
        }),
      ]);

      setPackages(packagesRes.data.packages);
      setPlans(plansRes.data.plans);

      try {
        const subRes = await axios.get<Subscription>(
          `${API}/v1/payments/subscription`,
          { withCredentials: true }
        );
        setSubscription(subRes.data);
      } catch {
        // No subscription
      }
    } catch (error) {
      console.error("Error loading pricing data:", error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async (packageId: string): Promise<void> => {
    if (!user) {
      navigate("/login");
      return;
    }
    setCheckoutLoading(packageId);
    try {
      const response = await axios.post<{ url: string }>(
        `${API}/v1/payments/checkout/credits`,
        { package: packageId },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || "Failed to start checkout");
      } else {
        toast.error("Failed to start checkout");
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleSubscribe = async (planId: string): Promise<void> => {
    if (!user) {
      navigate("/login");
      return;
    }
    setCheckoutLoading(planId);
    try {
      const response = await axios.post<{ url: string }>(
        `${API}/v1/payments/checkout/subscription`,
        { plan: planId },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || "Failed to start checkout");
      } else {
        toast.error("Failed to start checkout");
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = (): void => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async (): Promise<void> => {
    setShowCancelModal(false);
    setCancelingSubscription(true);
    try {
      await axios.post(
        `${API}/v1/payments/subscription/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success("Subscription canceled. Access continues until period ends.");
      // Update local state immediately
      setSubscription((prev) =>
        prev ? { ...prev, cancel_at_period_end: true } : null
      );
      // Try to reload data, but don't fail if subscription status fetch fails
      try {
        await loadData();
      } catch (loadError) {
        console.warn("Could not refresh data after cancel:", loadError);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error.response?.data?.detail || "Failed to cancel subscription"
        );
      } else {
        toast.error("Failed to cancel subscription");
      }
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleReactivateSubscription = async (): Promise<void> => {
    setReactivatingSubscription(true);
    try {
      await axios.post(
        `${API}/v1/payments/subscription/reactivate`,
        {},
        { withCredentials: true }
      );
      toast.success("Subscription reactivated! You will continue to be billed.");
      setSubscription((prev) =>
        prev ? { ...prev, cancel_at_period_end: false } : null
      );
      try {
        await loadData();
      } catch (loadError) {
        console.warn("Could not refresh data after reactivate:", loadError);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(
          error.response?.data?.detail || "Failed to reactivate subscription"
        );
      } else {
        toast.error("Failed to reactivate subscription");
      }
    } finally {
      setReactivatingSubscription(false);
    }
  };

  const handleSwitchPlan = (newPlanId: string): void => {
    const newPlan = plans.find((p) => p.id === newPlanId);
    const currentPlanId = subscription?.plan || user?.subscription_plan;
    const currentPlan = plans.find((p) => p.id === currentPlanId);
    const isUpgrade =
      newPlan !== undefined &&
      currentPlan !== undefined &&
      newPlan.price > currentPlan.price;

    setSwitchModal({ show: true, plan: newPlan || null, isUpgrade });
  };

  const confirmSwitchPlan = async (): Promise<void> => {
    const newPlan = switchModal.plan;
    if (!newPlan) return;

    setSwitchModal({ ...switchModal, show: false });
    setSwitchingPlan(newPlan.id);

    try {
      const response = await axios.post<{ message?: string }>(
        `${API}/v1/payments/subscription/change-plan`,
        { plan: newPlan.id },
        { withCredentials: true }
      );
      toast.success(response.data.message || `Switched to ${newPlan.name}!`);
      loadData();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || "Failed to switch plan");
      } else {
        toast.error("Failed to switch plan");
      }
    } finally {
      setSwitchingPlan(null);
    }
  };

  const isCurrentPlan = (planId: string): boolean => {
    return (
      (subscription?.has_subscription && subscription.plan === planId) ||
      (user?.is_premium === true && user?.subscription_plan === planId)
    );
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--st-primary)" }} />
        </div>
      </PageLayout>
    );
  }

  const premiumFeatures: PremiumFeature[] = [
    { icon: Volume2, label: "Audio Narration", desc: "AI voice for stories" },
    { icon: Palette, label: "Pro Layouts", desc: "Picture book styles" },
    { icon: ImageIcon, label: "Photo Transform", desc: "Turn photos to art" },
    {
      icon: Library,
      label: "Book Series",
      desc: "Unlimited series",
      isSeries: true,
    },
    { icon: Zap, label: "Priority Queue", desc: "Faster generation" },
  ];

  const faqItems: FAQItem[] = [
    {
      q: "What is a credit?",
      a: "One credit = one complete story with illustrations, a cover page, and optional audio narration.",
    },
    {
      q: "What are story versions (v1, v2, v3)?",
      a: "Story versions indicate the AI image quality: v1 (Standard) uses Flux AI, v2 (Premium) uses Gemini 2.5 with character consistency, and v3 (Pro) uses Gemini 3 Pro for the highest quality illustrations.",
    },
    {
      q: "How much does a Pro story cost?",
      a: "Pro stories (v3) cost 2 credits and use our highest quality AI illustrations. Only premium subscribers can access v3.",
    },
    {
      q: "Do credits expire?",
      a: "No! Credits never expire. Credit pack purchases and subscription credits both roll over — use them whenever you're ready.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes! You can cancel your subscription anytime. You'll keep access until your billing period ends, and any unused credits stay in your account.",
    },
    {
      q: "What happens if I switch plans?",
      a: "Upgrades are charged the prorated difference immediately. Downgrades apply a credit to your next invoice. Your existing credits are always preserved.",
    },
  ];

  const lostFeatures: string[] = [
    "Monthly credits",
    "Audio narration",
    "Pro picture book layouts",
    "Photo transform",
  ];

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              Credits & Subscriptions
            </h1>
            <p
              className="text-sm"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              Choose the plan that works best for your family
            </p>
            {user && (
              <div
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full"
                style={{ background: "var(--st-border-light)" }}
              >
                <Coins className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                <span
                  className="font-bold text-sm"
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                >
                  {user.credits} credits available
                </span>
              </div>
            )}
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-6">
            <div
              className="inline-flex rounded-xl p-1"
              style={{ background: "var(--st-border-light)" }}
            >
              <button
                onClick={() => setActiveTab("credits")}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  background: activeTab === "credits" ? "var(--st-bg-surface)" : "transparent",
                  color: activeTab === "credits" ? "var(--st-primary)" : "var(--st-text-muted)",
                  boxShadow:
                    activeTab === "credits"
                      ? "0 2px 8px rgba(0,0,0,0.08)"
                      : "none",
                }}
              >
                <Coins className="w-4 h-4" />
                Credit Packs
              </button>
              <button
                onClick={() => setActiveTab("subscription")}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  background:
                    activeTab === "subscription" ? "var(--st-bg-surface)" : "transparent",
                  color: activeTab === "subscription" ? "var(--st-primary)" : "var(--st-text-muted)",
                  boxShadow:
                    activeTab === "subscription"
                      ? "0 2px 8px rgba(0,0,0,0.08)"
                      : "none",
                }}
              >
                <Crown className="w-4 h-4" />
                Subscriptions
              </button>
            </div>
          </div>

          {/* Credit Packs Tab */}
          {activeTab === "credits" && (
            <div>
              <p
                className="text-center text-sm mb-6"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                One-time purchase. Credits never expire.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {packages.map((pkg, index) => {
                  const isPopular = index === 1;
                  const coinCount = index + 1; // 1 for Try It, 2 for Starter, 3 for Family, 4 for Power
                  return (
                    <div
                      key={pkg.id}
                      className="relative rounded-2xl p-5 text-center transition-all hover:shadow-lg dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]"
                      style={{
                        background: "var(--st-bg-surface)",
                        border: isPopular
                          ? "2px solid var(--st-primary)"
                          : "1px solid var(--st-border-light)",
                        boxShadow: isPopular
                          ? "0 8px 24px rgba(255,140,97,0.15)"
                          : undefined,
                      }}
                    >
                      {isPopular && (
                        <div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold"
                          style={{
                            background:
                              "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                            color: "white",
                            fontFamily: "Quicksand, sans-serif",
                          }}
                        >
                          Popular
                        </div>
                      )}
                      {/* Stacked Coins Icon */}
                      <div className="h-14 mx-auto flex items-center justify-center mb-3">
                        <div
                          className="flex items-end justify-center"
                          style={{ gap: "-4px" }}
                        >
                          {Array.from({ length: coinCount }).map((_, i) => (
                            <div
                              key={i}
                              className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white"
                              style={{
                                background: isPopular
                                  ? "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)"
                                  : "var(--st-border-light)",
                                marginLeft: i > 0 ? "-8px" : "0",
                                zIndex: coinCount - i,
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              }}
                            >
                              <Coins
                                className="w-4 h-4"
                                style={{ color: isPopular ? "white" : "var(--st-primary)" }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      <h3
                        className="font-bold mb-1"
                        style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                      >
                        {pkg.name}
                      </h3>
                      <p
                        className="text-2xl font-bold mb-1"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-primary)",
                        }}
                      >
                        {pkg.credits}
                      </p>
                      <p
                        className="text-xs mb-3"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-muted)",
                        }}
                      >
                        credits · ${(pkg.price / pkg.credits).toFixed(2)}/story
                      </p>
                      <button
                        onClick={() => handleBuyCredits(pkg.id)}
                        disabled={checkoutLoading === pkg.id}
                        className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 ${
                          !isPopular ? "bg-gray-100 dark:bg-white/10" : ""
                        }`}
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          background: isPopular
                            ? "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)"
                            : undefined,
                          color: isPopular ? "white" : "var(--st-text-heading)",
                        }}
                      >
                        {checkoutLoading === pkg.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : (
                          pkg.price_formatted
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === "subscription" && (
            <div>
              {/* Premium Features - Prominent Card */}
              <div
                className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10"
                style={{
                  border: "1px solid var(--st-border-medium)",
                }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5" style={{ color: "var(--st-primary)" }} />
                  <h3
                    className="font-bold"
                    style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                  >
                    Premium Features Included
                  </h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {premiumFeatures.map(
                    ({ icon: Icon, label, desc, isSeries }) => (
                      <div
                        key={label}
                        className={`rounded-xl p-3 text-center ${
                          isSeries ? "border border-indigo-200 dark:border-indigo-800" : ""
                        }`}
                        style={{
                          background: "var(--st-bg-surface)",
                          border: isSeries ? undefined : "1px solid var(--st-border-light)",
                        }}
                      >
                        <div
                          className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                            isSeries ? "bg-indigo-100 dark:bg-indigo-900/50" : ""
                          }`}
                          style={{ background: isSeries ? undefined : "var(--st-border-light)" }}
                        >
                          <Icon
                            className={`w-5 h-5 ${isSeries ? "text-indigo-600 dark:text-indigo-400" : ""}`}
                            style={{ color: isSeries ? undefined : "var(--st-primary)" }}
                          />
                        </div>
                        <p
                          className={`font-semibold text-xs ${isSeries ? "text-indigo-600 dark:text-indigo-400" : ""}`}
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            color: isSeries ? undefined : "var(--st-text-heading)",
                          }}
                        >
                          {label}
                        </p>
                        <p
                          className={`text-xs ${isSeries ? "text-indigo-500 dark:text-indigo-400" : ""}`}
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            color: isSeries ? undefined : "var(--st-text-muted)",
                          }}
                        >
                          {desc}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Current Subscription Banner */}
              {(subscription?.has_subscription || user?.is_premium) && (
                <div className="mb-6 space-y-3">
                  {/* Cancellation Warning */}
                  {subscription?.cancel_at_period_end && (
                    <div
                      className="p-4 rounded-2xl flex items-start gap-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700"
                    >
                      <AlertTriangle
                        className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400"
                      />
                      <div className="flex-1">
                        <p
                          className="font-semibold text-sm text-amber-800 dark:text-amber-200"
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                          }}
                        >
                          Your subscription is scheduled to cancel
                        </p>
                        <p
                          className="text-xs mt-1 text-amber-700 dark:text-amber-300"
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                          }}
                        >
                          You'll lose access to premium features on{" "}
                          <strong>
                            {subscription?.current_period_end
                              ? new Date(
                                  subscription.current_period_end
                                ).toLocaleDateString("en-US", {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "your billing date"}
                          </strong>
                          . Your unused credits will remain in your account.
                        </p>
                        <button
                          onClick={handleReactivateSubscription}
                          disabled={reactivatingSubscription}
                          className="mt-3 px-4 py-2 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                          style={{
                            background:
                              "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                            color: "white",
                            fontFamily: "Quicksand, sans-serif",
                          }}
                        >
                          {reactivatingSubscription ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4" />
                              Keep My Subscription
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Plan Info Card */}
                  <div
                    className={`p-4 rounded-2xl flex items-center justify-between ${
                      subscription?.cancel_at_period_end
                        ? "border border-yellow-400 dark:border-yellow-600"
                        : "border-2 border-purple-500"
                    }`}
                    style={{
                      background: "var(--st-bg-surface)",
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          subscription?.cancel_at_period_end
                            ? "bg-yellow-100 dark:bg-yellow-900/30"
                            : ""
                        }`}
                        style={{
                          background: subscription?.cancel_at_period_end
                            ? undefined
                            : "linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)",
                        }}
                      >
                        <Crown
                          className={`w-5 h-5 ${
                            subscription?.cancel_at_period_end
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-white"
                          }`}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-bold text-sm"
                            style={{
                              fontFamily: "Quicksand, sans-serif",
                              color: "var(--st-text-heading)",
                            }}
                          >
                            {subscription?.plan_name ||
                              (user?.subscription_plan
                                ? user.subscription_plan.charAt(0).toUpperCase() +
                                  user.subscription_plan.slice(1).replace("_sub", "")
                                : "Premium")}{" "}
                            Plan
                          </span>
                          {subscription?.cancel_at_period_end ? (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-amber-600 dark:text-amber-400"
                            >
                              Ending Soon
                            </span>
                          ) : (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                            >
                              Active
                            </span>
                          )}
                        </div>
                        <p
                          className="text-xs"
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            color: "var(--st-text-muted)",
                          }}
                        >
                          {subscription?.current_period_end
                            ? `${
                                subscription.cancel_at_period_end
                                  ? "Access until"
                                  : "Renews"
                              } ${new Date(
                                subscription.current_period_end
                              ).toLocaleDateString()}`
                            : "Active subscription"}
                        </p>
                      </div>
                    </div>
                    {!subscription?.cancel_at_period_end && (
                      <button
                        onClick={handleCancelSubscription}
                        disabled={cancelingSubscription}
                        className="text-sm font-medium px-4 py-2 rounded-lg transition-all hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-muted)",
                        }}
                      >
                        {cancelingSubscription ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Cancel"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Subscription Plans - Compact cards matching credit pack style */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {plans.map((plan, index) => {
                  const isBestValue = index === 1; // Standard plan
                  const isCurrent = isCurrentPlan(plan.id);
                  const crownCount = index + 1; // 1 for Lite, 2 for Standard, etc.
                  return (
                    <div
                      key={plan.id}
                      className={`relative rounded-2xl p-5 text-center transition-all hover:shadow-lg dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)] ${
                        isCurrent ? "bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/20" : ""
                      }`}
                      style={{
                        border: isCurrent
                          ? "2px solid #8B5CF6"
                          : isBestValue
                          ? "2px solid var(--st-primary)"
                          : "1px solid var(--st-border-light)",
                        boxShadow: isCurrent
                          ? "0 8px 24px rgba(139,92,246,0.15)"
                          : isBestValue
                          ? "0 8px 24px rgba(255,140,97,0.15)"
                          : undefined,
                        background: isCurrent ? undefined : "var(--st-bg-surface)",
                      }}
                    >
                      {isCurrent && (
                        <div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1"
                          style={{
                            background:
                              "linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)",
                            color: "white",
                            fontFamily: "Quicksand, sans-serif",
                          }}
                        >
                          <Check className="w-3 h-3" /> Current
                        </div>
                      )}
                      {!isCurrent && isBestValue && (
                        <div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold"
                          style={{
                            background:
                              "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                            color: "white",
                            fontFamily: "Quicksand, sans-serif",
                          }}
                        >
                          Popular
                        </div>
                      )}
                      {/* Stacked Crowns Icon - matches credit pack coin style */}
                      <div className="h-14 mx-auto flex items-center justify-center mb-3">
                        <div
                          className="flex items-end justify-center"
                          style={{ gap: "-4px" }}
                        >
                          {Array.from({ length: Math.min(crownCount, 4) }).map(
                            (_, i) => (
                              <div
                                key={i}
                                className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white"
                                style={{
                                  background: isCurrent
                                    ? "linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)"
                                    : isBestValue
                                    ? "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)"
                                    : "var(--st-border-light)",
                                  marginLeft: i > 0 ? "-8px" : "0",
                                  zIndex: crownCount - i,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                }}
                              >
                                <Crown
                                  className="w-4 h-4"
                                  style={{
                                    color:
                                      isCurrent || isBestValue
                                        ? "white"
                                        : "var(--st-primary)",
                                  }}
                                />
                              </div>
                            )
                          )}
                        </div>
                      </div>
                      <h3
                        className="font-bold mb-1"
                        style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                      >
                        {plan.name}
                      </h3>
                      <p
                        className="text-2xl font-bold mb-1"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-primary)",
                        }}
                      >
                        {plan.credits_per_month}
                      </p>
                      <p
                        className="text-xs mb-3"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-muted)",
                        }}
                      >
                        credits · $
                        {(plan.price / plan.credits_per_month).toFixed(2)}/story
                      </p>

                      {isCurrent ? (
                        <div
                          className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gray-100 dark:bg-white/10"
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            color: "var(--st-text-muted)",
                          }}
                        >
                          ${plan.price}/mo
                        </div>
                      ) : user?.is_premium || subscription?.has_subscription ? (
                        <button
                          onClick={() => handleSwitchPlan(plan.id)}
                          disabled={switchingPlan === plan.id}
                          className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
                            !isBestValue ? "bg-gray-100 dark:bg-white/10" : ""
                          }`}
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            background: isBestValue
                              ? "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)"
                              : undefined,
                            color: isBestValue ? "white" : "var(--st-text-heading)",
                          }}
                        >
                          {switchingPlan === plan.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            `$${plan.price}/mo`
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSubscribe(plan.id)}
                          disabled={checkoutLoading === plan.id}
                          className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 ${
                            !isBestValue ? "bg-gray-100 dark:bg-white/10" : ""
                          }`}
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            background: isBestValue
                              ? "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)"
                              : undefined,
                            color: isBestValue ? "white" : "var(--st-text-heading)",
                          }}
                        >
                          {checkoutLoading === plan.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                          ) : (
                            `$${plan.price}/mo`
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* FAQ - Shows on both tabs */}
          <div
            className="mt-8 rounded-2xl p-5"
            style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
          >
            <h3
              className="font-bold mb-4"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
              {faqItems.map((faq, idx) => (
                <div key={idx}>
                  <p
                    className="font-semibold text-sm mb-1"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    {faq.q}
                  </p>
                  <p
                    className="text-sm"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-muted)",
                    }}
                  >
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Switch Plan Modal */}
      {switchModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() =>
              setSwitchModal({ show: false, plan: null, isUpgrade: false })
            }
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl shadow-xl max-w-sm w-full overflow-hidden"
            style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
          >
            {/* Header */}
            <div className="p-5 pb-0">
              <div className="flex items-start justify-between">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{
                    background: switchModal.isUpgrade
                      ? "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)"
                      : "var(--st-border-light)",
                  }}
                >
                  {switchModal.isUpgrade ? (
                    <ArrowUp className="w-6 h-6 text-white" />
                  ) : (
                    <ArrowDown className="w-6 h-6" style={{ color: "var(--st-primary)" }} />
                  )}
                </div>
                <button
                  onClick={() =>
                    setSwitchModal({ show: false, plan: null, isUpgrade: false })
                  }
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <h3
                className="text-lg font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                {switchModal.isUpgrade ? "Upgrade Plan" : "Change Plan"}
              </h3>
              <p
                className="text-sm mb-4"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                {switchModal.isUpgrade
                  ? `Upgrade to ${switchModal.plan?.name}? You'll be charged the prorated difference for the remaining billing period.`
                  : `Switch to ${switchModal.plan?.name}? A credit will be applied to your next invoice for the difference.`}
              </p>

              {/* Plan Preview */}
              <div
                className="p-4 rounded-xl mb-4 bg-orange-50/50 dark:bg-white/5"
                style={{ border: "1px solid var(--st-border-light)" }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      background:
                        "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                    }}
                  >
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <span
                      className="font-bold text-sm"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      {switchModal.plan?.name} Plan
                    </span>
                    <p
                      className="text-xs"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-muted)",
                      }}
                    >
                      {switchModal.plan?.credits_per_month} credits/month
                    </p>
                  </div>
                  <span
                    className="font-bold"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    ${switchModal.plan?.price}
                    <span
                      className="text-xs font-normal"
                      style={{ color: "var(--st-text-muted)" }}
                    >
                      /mo
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() =>
                  setSwitchModal({ show: false, plan: null, isUpgrade: false })
                }
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSwitchPlan}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                  color: "white",
                }}
              >
                {switchModal.isUpgrade ? "Upgrade" : "Switch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowCancelModal(false)}
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl shadow-xl max-w-sm w-full overflow-hidden"
            style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
          >
            {/* Header */}
            <div className="p-5 pb-0">
              <div className="flex items-start justify-between">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/30"
                >
                  <Crown className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <h3
                className="text-lg font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Cancel Subscription?
              </h3>
              <p
                className="text-sm mb-4"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Are you sure you want to cancel? You'll keep access to premium
                features until the end of your billing period.
              </p>

              {/* What you'll lose */}
              <div
                className="p-4 rounded-xl mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
              >
                <p
                  className="text-xs font-semibold mb-2 text-red-600 dark:text-red-400"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                  }}
                >
                  You'll lose access to:
                </p>
                <ul className="space-y-1.5">
                  {lostFeatures.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-xs text-red-900 dark:text-red-200"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                      }}
                    >
                      <X className="w-3 h-3 text-red-600 dark:text-red-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                  color: "white",
                }}
              >
                Keep Subscription
              </button>
              <button
                onClick={confirmCancelSubscription}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default PricingPage;
